import { useState, useEffect } from "react";
import axios from "axios";
import { API, getAuthHeaders, useAuth } from "../context/AuthContext";
import { Key, Lock } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function AdminPasswordCard() {
  const { user } = useAuth();
  const [hasPassword, setHasPassword] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // If /auth/me didn't include password_hash, assume no password set on first load.
    // We infer from role: if user has password_hash field, field is stripped in response
    // so we rely on POST response. On mount we consider it unset unless told otherwise.
    // Simplest: call the endpoint with an invalid current password; if it says "current password required",
    // we know one is already set. Skip this complexity — always show "current" field but mark optional
    // until we know. We'll detect from the first POST.
    setHasPassword(user?._has_password ?? false);
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      const payload = { new_password: next };
      if (current) payload.current_password = current;
      await axios.post(`${API}/auth/set-password`, payload, { headers: getAuthHeaders() });
      toast.success("Password updated! Use it with your Gmail ID to sign in next time.");
      setCurrent(""); setNext(""); setConfirm("");
      setHasPassword(true);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to update password";
      if (detail.toLowerCase().includes("current password required")) {
        setHasPassword(true);
        toast.error("Enter your current password to change it");
      } else {
        toast.error(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="admin-password-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(245, 158, 11, 0.1)" }}>
          <Key size={20} weight="duotone" className="text-amber-400" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {hasPassword ? "Change Password" : "Set a Password"}
          </h3>
          <p className="text-[10px] text-[#A1A1AA] mt-0.5">
            {hasPassword
              ? "Update your admin password. Use it with your Gmail ID to sign in without Google."
              : "Set a password so you can sign in with email + password (in addition to Google)."}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3 max-w-sm">
        {hasPassword && (
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] mb-1 block">Current Password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
              <Input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required={hasPassword}
                autoComplete="current-password"
                data-testid="admin-current-password"
                className="pl-9 bg-[#0F0F0F] border-white/10 text-sm text-white"
              />
            </div>
          </div>
        )}
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] mb-1 block">New Password</label>
          <div className="relative">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="admin-new-password"
              placeholder="min 8 characters"
              className="pl-9 bg-[#0F0F0F] border-white/10 text-sm text-white"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] mb-1 block">Confirm Password</label>
          <div className="relative">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="admin-confirm-password"
              className="pl-9 bg-[#0F0F0F] border-white/10 text-sm text-white"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="admin-save-password"
          className="px-5 py-2 rounded-md text-xs font-bold bg-amber-500 text-black hover:bg-amber-400 transition-all disabled:opacity-50"
        >
          {saving ? "Saving..." : (hasPassword ? "Update Password" : "Set Password")}
        </button>
      </form>
    </div>
  );
}
